import {
  createContext,
  FunctionComponent,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
  useMemo,
} from 'react';
import { useSelector } from 'react-redux';
import {
  ESRIMapOptions,
  GoogleMapOptions,
} from '@rsos/components/capstone/common/Map/types';
import { DEFAULT_ZOOM_LEVEL } from '@rsos/maps/constants';
import { ESRI, GOOGLE } from '@rsos/maps/constants/mapProviders';
import { StateWithEmergencyCalls } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { useSelectedCall } from '@rsos/reduxDataStore/src/features/selectors/callQueueSelector';
import { getMostRecentLocationWithLocation } from '@rsos/utils/getMostRecentLoc';
import { isLocationCentered } from '@rsos/utils/isLocationCentered';
import { panMapTo } from '@rsos/utils/panMapToCoordinate';

interface Location {
  latitude: number;
  longitude: number;
}

export type ZoomAndLocationContextData = {
  userPannedAway: boolean;
  zoomLevel: number;
  setMap: (map: ESRIMapOptions | GoogleMapOptions) => void;
};

// Default value so that CSP doesn't have to include the context
const defaultData: ZoomAndLocationContextData = {
  userPannedAway: false,
  zoomLevel: DEFAULT_ZOOM_LEVEL,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setMap: () => {},
};

export const ZoomAndLocationContext =
  createContext<ZoomAndLocationContextData>(defaultData);

/**
 * Context provider for preserving the zoom and location across user interactions
 *
 * When the user pans away from the most recent location we need to reflect
 * that in the `userPannedAway` state.  This state is used by various map
 * marker components to determine whether to pan the map to the most recent
 * location when new locations are ingested.  If the user has panned away,
 * we do not want to snap the map to the new locations as they come in.
 * This state needs to survive across changes to map providers.
 *
 * This context also keeps track of the point the map is centered and the
 * current zoom level. In the event the map provider changes there is a
 * useEffect that will pan the map to the same location the previous map
 * provider was centered, and zoom in to the same zoom level.
 */
export const ZoomAndLocationContextProvider: FunctionComponent<
  PropsWithChildren
> = ({ children }) => {
  const [map, setMap] = useState<ESRIMapOptions | GoogleMapOptions | undefined>(
    undefined,
  );

  /**
   * Update userPannedAway based on the most recent location
   */
  const [userPannedAway, setUserPannedAway] = useState(false);
  const selectedCall = useSelectedCall();
  const callerID = useSelector(
    (state: StateWithEmergencyCalls) => state.emergencyCalls.selectedCallerID,
  );
  const mostRecentLocation = useMemo(
    () => getMostRecentLocationWithLocation(selectedCall),
    [selectedCall],
  );

  const checkIfUserPannedAway = useCallback(() => {
    if (!map) {
      return;
    }

    setUserPannedAway(
      Boolean(
        mostRecentLocation && !isLocationCentered(mostRecentLocation, map),
      ),
    );
  }, [mostRecentLocation, map]);

  useEffect(() => {
    // Reset userPannedAway if the selected call changes.
    setUserPannedAway(false);
  }, [callerID]);

  /**
   * Track the zoomLevel when the user pans in or out
   */
  const [zoomLevel, setZoomLevel] = useState(DEFAULT_ZOOM_LEVEL);
  const updateZoomLevel = useCallback(() => {
    if (!map) {
      // Map instance can be undefined on loading.
      // This cannot be fixed without major refactor.
      return;
    }

    if (map.mapProvider === GOOGLE) {
      const newZoomLevel = map.mapInstance.getZoom();

      if (!newZoomLevel) {
        throw new Error('Failed to get the Google map zoom level');
      }

      setZoomLevel(newZoomLevel);
    } else if (map.mapProvider === ESRI) {
      if (!map.mapInstance.view) {
        throw new Error('ESRI map view not initialized');
      }
      setZoomLevel(map.mapInstance.view.zoom);
    }
  }, [map]);

  /**
   * Track when the center lat / long change
   */
  const [location, setLocation] = useState<Location | undefined>(undefined);
  const updateCoordinates = useCallback(() => {
    if (!map) {
      // Map instance can be undefined on loading.
      // This cannot be fixed without major refactor.
      return;
    }

    if (map.mapProvider === GOOGLE) {
      const center = map.mapInstance.getCenter();

      if (!center) {
        throw new Error('Failed to get the Google map center location');
      }

      setLocation({ latitude: center.lat(), longitude: center.lng() });
    } else if (map.mapProvider === ESRI) {
      if (!map.mapInstance.view) {
        throw new Error('ESRI map view not initialized');
      }
      setLocation({
        latitude: map.mapInstance.view.center.latitude,
        longitude: map.mapInstance.view.center.longitude,
      });
    }
  }, [map]);

  /**
   * If the map provider changes preserve the zoom level and map center
   */
  useEffect(() => {
    if (!map || !location) {
      return;
    }

    panMapTo({
      ...map,
      ...location,
      zoom: zoomLevel,
    });
    // We only want to pan the map when the mapProvider changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  /**
   * Set the values for latitude / longitude once map is set for the first time
   */
  useEffect(() => {
    if (!map) return;

    if (location === undefined) {
      if (map.mapProvider === GOOGLE) {
        const center = map.mapInstance.getCenter();

        if (!center) {
          throw new Error('Failed to get the Google map center location');
        }

        setLocation({ latitude: center.lat(), longitude: center.lng() });
      } else if (
        map.mapProvider === ESRI &&
        map.mapInstance.view &&
        map.mapInstance.view.center
      ) {
        setLocation({
          latitude: map.mapInstance.view.center.latitude,
          longitude: map.mapInstance.view.center.longitude,
        });
      }
    }
  }, [map, location]);

  /**
   * Register callbacks with the map for when center or zoom changes.
   * Zoom updates only fire once the map stops moving:
   *   - For Google we listen to `idle` (fires after pan/zoom settles).
   *   - For ESRI we use the view's `stationary` property.
   * Center / pan-away checks still run on every bounds change so callouts
   * like "user panned away" remain responsive.
   */
  useEffect(() => {
    if (!map) {
      // Map instance can be undefined on loading.
      // This cannot be fixed without major refactor.
      return;
    }

    const listeners: google.maps.MapsEventListener[] | __esri.WatchHandle[] =
      [];

    if (map.mapProvider === GOOGLE) {
      listeners.push(
        map.mapInstance.addListener('bounds_changed', updateCoordinates),
        map.mapInstance.addListener('bounds_changed', checkIfUserPannedAway),
        map.mapInstance.addListener('idle', updateZoomLevel),
      );
    } else if (map.mapProvider === ESRI) {
      if (!map.mapInstance.view) {
        throw new Error('ESRI map view not initialized');
      }

      // Use ESRI's stationary property - only update when the view stops moving
      listeners.push(
        map.mapInstance.view.watch('stationary', (isStationary: boolean) => {
          if (isStationary) {
            updateCoordinates();
            updateZoomLevel();
            checkIfUserPannedAway();
          }
        }),
      );
    }

    return () => {
      listeners.forEach(listener => {
        listener.remove();
      });
    };
  }, [map, updateCoordinates, updateZoomLevel, checkIfUserPannedAway]);

  return (
    <ZoomAndLocationContext.Provider
      value={{ setMap, userPannedAway, zoomLevel }}
    >
      {children}
    </ZoomAndLocationContext.Provider>
  );
};

export const useZoomAndLocationContext = () => {
  return useContext(ZoomAndLocationContext);
};
